/** @jsx jsx */
import { jsx } from '@emotion/react';

import type * as React from 'react';

import { useQuery } from '@apollo/client';

import RelatedSkillsContext from 'bundles/browse-common/contexts/RelatedSkillsContext';
import DomainRelatedSkillsQuery from 'bundles/browse-common/queries/DomainRelatedSkillsQuery.graphql';
import type {
  DomainRelatedSkillsQuery as DomainRelatedSkillsQueryType,
  DomainRelatedSkillsQueryVariables,
} from 'bundles/browse-common/queries/__generated__/DomainRelatedSkillsQuery';
import type { Domain } from 'bundles/browse-common/types/domain';

// from https://looker.dkandu.me/explore/Skill_Graph/enrollments_skills?toggle=fil&qid=JdeR79jtlRQQe5J4kgEQgy
const TOP_SKILLS_BY_ENROLLMENT_LAST_28D = [
  'Data Analysis',
  'Analysis',
  'Leadership and Management', // id: management
  'Artificial Intelligence',
  'Communication',
  'Generative AI',
  'Computer Programming',
  'Machine Learning',
  'Strategic Thinking', // id: strategy
  'Data Ethics',
  'Python Programming', // id: python-programming-language
  'Critical Thinking',
  'Prompt Engineering',
  'Planning',
  'Microsoft Excel',
  'Problem Solving',
  'Language Learning', // id: language from the looker but not found
  'Data Visualization', // id: visualization-graphic
  'Project Management',
  'Spreadsheet Software',
];
type Props = {
  domains?: Domain[];
  children: React.ReactNode;
};

function useRelatedSkillsQuery(domainIds: string[]) {
  return useQuery<DomainRelatedSkillsQueryType, DomainRelatedSkillsQueryVariables>(DomainRelatedSkillsQuery, {
    variables: {
      request: {
        domainIds,
        includeOnlyEligibleForScoring: false,
        minimumNumberOfRecs: 10,
        skillNames: domainIds.length === 0 ? TOP_SKILLS_BY_ENROLLMENT_LAST_28D : undefined,
        skillSorts: ['GRANULARITY_ASC'],
      },
      limit: domainIds.length === 0 ? 40 : 20, // need 40 to get all of the top skills by enrollment
    },
    context: {
      clientName: 'gatewayGql',
    },
    errorPolicy: 'all',
  });
}

const RelatedSkillsProvider = (props: Props) => {
  const { children, domains = [] } = props;

  const domainIds = domains.map((domain) => domain.slug);

  const { data } = useRelatedSkillsQuery(domainIds);

  const skills = data?.Skills?.querySkills.elements;
  const sortedSkills = skills
    ?.slice()
    ?.sort((a, b) => {
      const aIndex = TOP_SKILLS_BY_ENROLLMENT_LAST_28D.indexOf(a?.skillName ?? '');
      const bIndex = TOP_SKILLS_BY_ENROLLMENT_LAST_28D.indexOf(b?.skillName ?? '');
      return (aIndex === -1 ? 999 : aIndex) - (bIndex === -1 ? 999 : bIndex);
    })
    ?.slice(0, 20);

  return (
    <RelatedSkillsContext.Provider value={{ skills: domainIds.length === 0 ? sortedSkills : skills }}>
      {children}
    </RelatedSkillsContext.Provider>
  );
};

export default RelatedSkillsProvider;
